/**
 * In-app numeric keypad for phone entry. The phone's own keyboard never opens:
 * every key is a plain button, and the fields it types into are not inputs.
 *
 *   1 2 3 ⌫
 *   4 5 6 [secondary]   (Skip on water readings; ½ on application amounts)
 *   7 8 9 [action]      (Next / Save, spans two rows)
 *   . 0 Clear          ("." is "/" on a pair field like alkalinity 100/110)
 */
import type { ReactNode } from 'react';
import { Delete } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface KeypadKey {
  label: ReactNode;
  ariaLabel?: string;
  onPress: () => void;
  disabled?: boolean;
}

interface KeypadProps {
  onDigit: (char: string) => void;
  onDelete: () => void;
  onClear: () => void;
  secondaryKey: KeypadKey;
  actionKey: KeypadKey;
  digitsDisabled?: boolean;
  /**
   * Greys the "." key: the value takes whole numbers only. A press is still reported to onDigit,
   * so the caller can refuse the digits typed after it instead of reading "4 . 5" as 45.
   */
  decimalDisabled?: boolean;
  /** A pair field ("100/110"): the "." key becomes "/", reported to onDigit as "/". */
  slashKey?: boolean;
  /** Read out by screen readers whenever it changes, e.g. "pH, 8.2". */
  announcement: string;
  className?: string;
}

const keyBase =
  'flex min-h-[52px] min-w-[44px] items-center justify-center rounded-lg text-xl font-semibold touch-manipulation select-none ' +
  'transition-colors disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';
const digitKey = cn(keyBase, 'bg-white text-gray-900 shadow-[0_1px_0_#d1d5db] active:bg-gray-100');
const altKey = cn(keyBase, 'bg-gray-200 text-base text-gray-800 active:bg-gray-300');
const goKey = cn(keyBase, 'row-span-2 bg-primary-600 text-base text-white active:bg-primary-700');

export function Keypad({
  onDigit,
  onDelete,
  onClear,
  secondaryKey,
  actionKey,
  digitsDisabled,
  decimalDisabled,
  slashKey,
  announcement,
  className,
}: KeypadProps) {
  const digit = (d: string, ariaLabel?: string, inert?: boolean) => (
    <button
      key={d}
      type="button"
      className={cn(digitKey, inert && 'opacity-40 active:bg-white')}
      aria-label={ariaLabel ?? d}
      aria-disabled={inert || undefined}
      disabled={digitsDisabled}
      onClick={() => onDigit(d)}
    >
      {d}
    </button>
  );
  const extra = (k: KeypadKey, cls: string) => (
    <button
      type="button"
      className={cls}
      aria-label={k.ariaLabel}
      disabled={k.disabled}
      onClick={k.onPress}
    >
      {k.label}
    </button>
  );

  return (
    <div
      className={cn(
        'grid grid-cols-4 gap-1.5 bg-gray-300 px-2 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]',
        className
      )}
      role="group"
      aria-label="Keypad"
      data-keypad
    >
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>
      {digit('1')}
      {digit('2')}
      {digit('3')}
      <button type="button" className={altKey} aria-label="Delete" disabled={digitsDisabled} onClick={onDelete}>
        <Delete className="h-6 w-6" aria-hidden="true" />
      </button>
      {digit('4')}
      {digit('5')}
      {digit('6')}
      {extra(secondaryKey, altKey)}
      {digit('7')}
      {digit('8')}
      {digit('9')}
      {extra(actionKey, goKey)}
      {slashKey ? digit('/', 'Slash, between the two numbers') : digit('.', 'Decimal point', decimalDisabled)}
      {digit('0')}
      <button type="button" className={altKey} onClick={onClear}>
        Clear
      </button>
    </div>
  );
}
